/**
 * FIN-11096 — lodash removal shim.
 *
 * Drop-in replacements for the subset of lodash functions used in AMLS frontend.
 * Each helper mirrors lodash 4.x semantics ONLY for the input shapes the audit
 * (lodash-removal-audit-plan.md) found at AMLS call-sites. Not a full lodash port.
 *
 * Adding a helper here:
 *   1. Write a failing test in tests/lodashlike.test.js first (red)
 *   2. Implement here, run jest --testPathPattern=lodashlike (green)
 *   3. Commit before migrating call-sites to use it
 */

/**
 * Returns true for: null, undefined, empty array, empty object, empty string,
 * and primitives that have no own enumerable properties (0, false, NaN — lodash
 * quirk preserved so future call-sites can't silently diverge).
 *
 * Uses Object.keys (own-enumerable only) so a polluted Object.prototype does
 * not change the result. See tests/prototypePollution.test.js for the broader
 * WEB-02 coverage.
 */
// JSON-safe deep clone. Does NOT support Moment.js objects — those call-sites
// must use moment(original) directly (see lodash-removal-audit-plan.md §cloneDeep).
export const cloneDeep = (value) => {
  if (value === null || typeof value !== 'object') return value;
  return JSON.parse(JSON.stringify(value));
};

export const isArray = Array.isArray;
export const isNull = (value) => value === null;
export const isBoolean = (value) => typeof value === 'boolean';
export const isNumber = (value) => typeof value === 'number';
export const noop = () => undefined;
export const toLower = (str) => (str == null ? '' : String(str).toLowerCase());

export const round = (value, precision = 0) => {
  const factor = Math.pow(10, precision);
  return Math.round(value * factor) / factor;
};

export const floor = (value, precision = 0) => {
  const factor = Math.pow(10, precision);
  return Math.floor(value * factor) / factor;
};

export const pickBy = (obj, predicate = Boolean) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => predicate(v))
  );
};

export const castArray = (value) => {
  if (Array.isArray(value)) return value;
  return [value];
};

export const keyBy = (arr, iteratee) => {
  if (arr == null) return {};
  const fn =
    typeof iteratee === 'function' ? iteratee : (item) => item[iteratee];
  return arr.reduce((acc, item) => {
    acc[fn(item)] = item;
    return acc;
  }, {});
};

export const groupBy = (arr, iteratee) => {
  if (arr == null) return {};
  const fn =
    typeof iteratee === 'function' ? iteratee : (item) => item[iteratee];
  return arr.reduce((acc, item) => {
    const key = fn(item);
    (acc[key] = acc[key] || []).push(item);
    return acc;
  }, {});
};

export const orderBy = (arr, keys, dirs) => {
  if (arr == null) return [];
  const copy = [...arr];
  copy.sort((a, b) => {
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const dir = dirs && dirs[i] === 'desc' ? -1 : 1;
      const av = typeof key === 'function' ? key(a) : a[key];
      const bv = typeof key === 'function' ? key(b) : b[key];
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
    }
    return 0;
  });
  return copy;
};

let _uniqueIdCounter = 0;
export const uniqueId = (prefix = '') => `${prefix}${++_uniqueIdCounter}`;

export const xor = (a, b) => {
  const setA = new Set(a);
  const setB = new Set(b);
  return [...a.filter((x) => !setB.has(x)), ...b.filter((x) => !setA.has(x))];
};

export const uniqBy = (arr, iteratee) => {
  const fn =
    typeof iteratee === 'function' ? iteratee : (item) => item[iteratee];
  const seen = new Set();
  return arr.filter((item) => {
    const key = fn(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const forOwn = (obj, fn) => {
  if (obj == null) return;
  Object.keys(obj).forEach((key) => fn(obj[key], key, obj));
};

export const omit = (obj, keys) => {
  // FIN-11819: lodash _.omit(undefined/null, ...) silently returns {}.
  // Native Object.entries throws on undefined/null — mirror lodash to
  // avoid crashing render paths that hand us optional props (e.g.
  // CustomMultiSelect's customProps in the QueryBuilder).
  if (obj == null) return {};
  const set = new Set(keys);
  return Object.fromEntries(Object.entries(obj).filter(([k]) => !set.has(k)));
};

export const result = (obj, path, defaultValue) => {
  if (obj == null) return defaultValue;
  const value = String(path)
    .split('.')
    .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
  if (value === undefined) return defaultValue;
  return typeof value === 'function' ? value.call(obj) : value;
};

export const isEqual = (a, b) => {
  if (a === b) return true;
  if (
    a === null ||
    b === null ||
    typeof a !== 'object' ||
    typeof b !== 'object'
  )
    return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(
    (k) => Object.prototype.hasOwnProperty.call(b, k) && isEqual(a[k], b[k])
  );
};

export const startCase = (str) => {
  if (str == null) return '';
  return String(str)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[\s_\-]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

export const truncate = (str, options = {}) => {
  if (str == null) return '';
  const s = String(str);
  const { length = 30, omission = '...' } = options;
  if (s.length <= length) return s;
  return s.slice(0, length - omission.length) + omission;
};

export const capitalize = (str) => {
  if (str == null) return '';
  const s = String(str);
  if (s.length === 0) return '';
  return s[0].toUpperCase() + s.slice(1).toLowerCase();
};

export const isEmpty = (value) => {
  if (value == null) return true;
  if (Array.isArray(value) || typeof value === 'string')
    return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return true;
};

export const keys = (obj) => (obj == null ? [] : Object.keys(obj));
export const values = (obj) => (obj == null ? [] : Object.values(obj));
export const uniq = (arr) => [...new Set(arr)];
export const isUndefined = (value) => value === undefined;
export const has = (obj, key) =>
  obj != null && Object.prototype.hasOwnProperty.call(obj, key);
export const isNaN = Number.isNaN;
export const trim = (str, chars) => {
  if (str == null) return '';
  const s = String(str);
  if (!chars) return s.trim();
  const escaped = chars.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  return s.replace(new RegExp(`^[${escaped}]+|[${escaped}]+$`, 'g'), '');
};

export const includes = (collection, value) => collection.includes(value);

export const isPlainObject = (value) => {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

export const lowerCase = (str) => {
  if (str == null) return '';
  return String(str)
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .toLowerCase();
};

export const without = (arr, ...values) => {
  const set = new Set(values);
  return arr.filter((x) => !set.has(x));
};

export const sortBy = (arr, keys) => {
  if (arr == null) return [];
  const ks = Array.isArray(keys) ? keys : [keys];
  return [...arr].sort((a, b) => {
    for (const k of ks) {
      const av = typeof k === 'function' ? k(a) : a[k];
      const bv = typeof k === 'function' ? k(b) : b[k];
      if (av < bv) return -1;
      if (av > bv) return 1;
    }
    return 0;
  });
};

// Converts a lodash iteratee shorthand into a function:
//   function  → returned as-is
//   object    → matches(item) = every [k,v] in obj satisfies item[k] === v
//   [key,val] → item => item[key] === val
//   string    → item => item[str]  (truthy check)
const toIteratee = (pred) => {
  if (typeof pred === 'function') return pred;
  if (Array.isArray(pred)) return (item) => item[pred[0]] === pred[1];
  if (pred !== null && typeof pred === 'object')
    return (item) => Object.entries(pred).every(([k, v]) => item[k] === v);
  return (item) => item[pred];
};

export const find = (arr, pred) => arr.find(toIteratee(pred));

export const filter = (arr, pred) => arr.filter(toIteratee(pred));

export const findIndex = (arr, pred) => arr.findIndex(toIteratee(pred));

export const forEach = (collection, fn) => {
  if (collection == null) return;
  if (Array.isArray(collection)) {
    collection.forEach(fn);
  } else {
    Object.keys(collection).forEach((k) => fn(collection[k], k, collection));
  }
};

export const split = (str, sep, limit) => String(str).split(sep, limit);

// eslint-disable-next-line no-shadow
export const parseInt = Number.parseInt;
export const toNumber = (value) => Number(value);

export const replace = (str, pattern, replacement) => {
  if (str == null) return '';
  return String(str).replace(pattern, replacement);
};

export const last = (arr) =>
  arr && arr.length ? arr[arr.length - 1] : undefined;
export const head = (arr) => (arr && arr.length ? arr[0] : undefined);
export const flatten = (arr) => arr.flat();
export const compact = (arr) => arr.filter(Boolean);
export const join = (arr, sep = ',') => arr.join(sep);

export const pick = (obj, ks) => {
  const set = new Set(ks);
  return Object.fromEntries(Object.entries(obj).filter(([k]) => set.has(k)));
};

export const chunk = (arr, size) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size)
    result.push(arr.slice(i, i + size));
  return result;
};

export const map = (collection, iteratee) => {
  if (collection == null) return [];
  const fn = typeof iteratee === 'string' ? (item) => item[iteratee] : iteratee;
  if (Array.isArray(collection)) return collection.map(fn);
  return Object.keys(collection).map((k) => fn(collection[k], k, collection));
};

export const union = (...arrays) => [...new Set(arrays.flat())];

export const unionBy = (a, b, iteratee) => {
  if (!iteratee) return [...new Set([...a, ...b])];
  const fn = typeof iteratee === 'function' ? iteratee : (x) => x[iteratee];
  const seen = new Set();
  return [...a, ...b].filter((item) => {
    const key = fn(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const uniqWith = (arr, comparator) =>
  arr.filter(
    (item, i) => arr.findIndex((other) => comparator(item, other)) === i
  );

export const flatMap = (arr, fn) => (arr == null ? [] : arr.flatMap(fn));

export const countBy = (arr, iteratee) => {
  const fn =
    iteratee == null
      ? (x) => x
      : typeof iteratee === 'function'
      ? iteratee
      : (x) => x[iteratee];
  return arr.reduce((acc, item) => {
    const key = fn(item);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
};

export const mapKeys = (obj, fn) =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [fn(v, k, obj), v]));

export const concat = (arr, ...items) => arr.concat(...items);

export const pullAll = (arr, values) => {
  const set = new Set(values);
  let i = arr.length;
  while (i--) {
    if (set.has(arr[i])) arr.splice(i, 1);
  }
  return arr;
};

export const clone = (value) => {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return [...value];
  return Object.assign({}, value);
};

export const times = (n, fn) => Array.from({ length: n }, (_, i) => fn(i));

export const assignInWith = (object, source, customizer) => {
  Object.keys(source).forEach((key) => {
    object[key] = customizer(object[key], source[key], key, object, source);
  });
  return object;
};

export const partialRight =
  (fn, ...partialArgs) =>
  (...args) =>
    fn(...args, ...partialArgs);

export const every = (arr, predicate = Boolean) => arr.every(predicate);

export const get = (obj, path, defaultValue) => {
  if (obj == null) return defaultValue;
  const parts = Array.isArray(path) ? path : String(path).split('.');
  let cur = obj;
  for (const key of parts) {
    if (cur == null) return defaultValue;
    cur = cur[key];
  }
  return cur === undefined ? defaultValue : cur;
};

export const reduce = (collection, fn, initialValue) => {
  if (collection == null) return initialValue;
  if (Array.isArray(collection)) return collection.reduce(fn, initialValue);
  return Object.keys(collection).reduce(
    (acc, key) => fn(acc, collection[key], key, collection),
    initialValue
  );
};

export const sum = (arr) => arr.reduce((a, b) => a + b, 0);

export const debounce = (fn, wait) => {
  let timer;
  const debounced = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
  debounced.cancel = () => clearTimeout(timer);
  return debounced;
};
